import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import type {
  IConfidenceIntervalSliderData,
  IGlobalConfidenceRange
} from './m-confidence-interval-slider.types';
import { colors } from '../../styles/colors/colors.config';
import { MTooltipComponent } from '../m-tooltip/m-tooltip.component';
import { MBellCurveTooltipComponent } from '../m-bell-curve-tooltip/m-bell-curve-tooltip.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-confidence-interval-slider',
  templateUrl: './m-confidence-interval-slider.component.html',
  styleUrls: ['./m-confidence-interval-slider.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [MTooltipComponent, MBellCurveTooltipComponent]
})
export class MConfidenceIntervalSliderComponent implements OnInit {
  @Input() data!: IConfidenceIntervalSliderData;
  @Input() globalRange?: IGlobalConfidenceRange;
  @Input() showLabels = true;

  // Constants for minimum dimensions
  private readonly MIN_RANGE_WIDTH_REM = 2.5; // 40px
  private readonly MIN_RANGE_HEIGHT_REM = 0.75; // 12px

  // Calculated properties
  calculatedMin = 0;
  calculatedMax = 0;
  sliderValue = 0;
  confidenceBarColor = '';

  // Tooltip data for bell curve display
  tooltipData: {
    metricValue: string;
    lowerBound: string;
    upperBound: string;
    statisticalSignificance: string;
  } | null = null;

  ngOnInit(): void {
    this.calculateRange();
    this.setSliderValue();
    this.setConfidenceColor();
    this.prepareTooltipData();
  }

  private calculateRange(): void {
    if (this.globalRange) {
      // Apply default padding to global range for better visual consistency
      const globalRange =
        this.globalRange.globalMax - this.globalRange.globalMin;
      const defaultPadding = globalRange * 0.6; // 60% padding factor

      this.calculatedMin = this.globalRange.globalMin - defaultPadding;
      this.calculatedMax = this.globalRange.globalMax + defaultPadding;
    } else {
      const range = this.data.upperBound - this.data.lowerBound;
      const padding = this.calculateAdaptivePadding(
        range,
        this.data.lowerBound,
        this.data.upperBound
      );

      // Calculate bounds with padding, handling all negative/positive scenarios
      const paddedMin = this.data.lowerBound - padding;
      const paddedMax = this.data.upperBound + padding;

      // Apply intelligent bounds calculation based on the data characteristics
      this.calculatedMin =
        this.data.min ??
        this.calculateIntelligentMin(paddedMin, this.data.lowerBound);
      this.calculatedMax = this.data.max ?? paddedMax;
    }
  }

  private calculateIntelligentMin(
    paddedMin: number,
    originalLowerBound: number
  ): number {
    // Case 1: Both bounds are positive or zero - allow going to zero but not negative
    if (originalLowerBound >= 0) {
      return Math.max(0, paddedMin);
    }

    // Case 2: Both bounds are negative - allow full negative range
    // Case 3: Mixed bounds (negative lower, positive upper) - allow full range
    // In both cases, preserve the full range without artificial clamping
    return paddedMin;
  }

  private calculateAdaptivePadding(
    range: number,
    lowerBound: number,
    upperBound: number
  ): number {
    const scalingFactor = 0.6; // Base 60% scaling factor

    // Calculate percentage-based padding
    const percentagePadding = range * scalingFactor;

    // Define minimum absolute padding based on the magnitude of values
    let minAbsolutePadding: number;

    const maxValue = Math.max(Math.abs(lowerBound), Math.abs(upperBound));

    if (maxValue >= 100) {
      // For large values (≥100), use larger minimum padding
      minAbsolutePadding = 10;
    } else if (maxValue >= 10) {
      // For medium values (10-99), use medium minimum padding
      minAbsolutePadding = 1;
    } else if (maxValue >= 1) {
      // For values around 1, use smaller minimum padding
      minAbsolutePadding = 0.1;
    } else {
      // For decimal values (<1), use very small but adequate minimum padding
      minAbsolutePadding = 0.05;
    }

    // Use the larger of percentage-based or minimum absolute padding
    return Math.max(percentagePadding, minAbsolutePadding);
  }

  private setSliderValue(): void {
    // Ensure metric value is always within bounds for proper display
    this.sliderValue = Math.max(
      this.data.lowerBound,
      Math.min(this.data.upperBound, this.data.metricValue)
    );
  }

  private setConfidenceColor(): void {
    // Convert to percentage if it's in decimal format (0-1)
    const confidencePercent = this.data.confidenceLevel * 100;
    if (confidencePercent >= 80) {
      this.confidenceBarColor = colors['green-600']!; // Green (≥80%)
    } else if (confidencePercent >= 50) {
      this.confidenceBarColor = colors['green-400']!; // Light Green (50–79%)
    } else {
      this.confidenceBarColor = colors['gray-400']!; // Gray (<50%)
    }
  }

  formatValue(value: number): string {
    if (value == null || isNaN(value)) {
      return '$0.00';
    }

    const currency = this.data.currency || '$';
    const precision = this.data.precision ?? 2;

    const formattedNumber = value.toFixed(precision);

    if (value < 0) {
      return `-${currency}${Math.abs(value).toFixed(precision)}`;
    } else {
      return `${currency}${formattedNumber}`;
    }
  }

  private prepareTooltipData(): void {
    const confidencePercent =
      this.data.confidenceLevel <= 1
        ? this.data.confidenceLevel * 100
        : this.data.confidenceLevel;

    this.tooltipData = {
      metricValue: this.formatValue(this.data.metricValue),
      lowerBound: this.formatValue(this.data.lowerBound),
      upperBound: this.formatValue(this.data.upperBound),
      statisticalSignificance: `${Math.round(confidencePercent)}%`
    };
  }

  /**
   * Get confidence percentage for display
   */
  getConfidencePercentage(): string {
    const confidencePercent =
      this.data.confidenceLevel <= 1
        ? this.data.confidenceLevel * 100
        : this.data.confidenceLevel;
    return `${Math.round(confidencePercent)}%`;
  }

  getPositionPercentage(value: number): number {
    const range = this.calculatedMax - this.calculatedMin;
    if (range === 0) return 50; // Center if no range

    // Clamp the value within the confidence interval bounds (lowerBound and upperBound)
    // instead of the calculated min/max range
    const clampedValue = Math.max(
      this.data.lowerBound,
      Math.min(this.data.upperBound, value)
    );

    return ((clampedValue - this.calculatedMin) / range) * 100;
  }

  getRangeWidthPercentage(): number {
    const range = this.calculatedMax - this.calculatedMin;
    if (range === 0) return 0;
    return ((this.data.upperBound - this.data.lowerBound) / range) * 100;
  }

  getMetricPositionInRange(): number {
    const rangeWidth = this.data.upperBound - this.data.lowerBound;
    if (rangeWidth === 0) return 50; // Center if no range

    // Ensure metric value is within bounds before calculating position
    const clampedMetricValue = Math.max(
      this.data.lowerBound,
      Math.min(this.data.upperBound, this.data.metricValue)
    );

    return ((clampedMetricValue - this.data.lowerBound) / rangeWidth) * 100;
  }

  /**
   * Calculate the actual width for the confidence range bar
   * Ensures minimum width of 2.5rem (40px) is maintained
   */
  getCalculatedRangeWidth(): string {
    const percentageWidth = this.getRangeWidthPercentage();
    const calculatedWidth = `${percentageWidth}%`;

    // Apply minimum width constraint
    return `max(${this.MIN_RANGE_WIDTH_REM}rem, ${calculatedWidth})`;
  }

  /**
   * Calculate the actual height for the confidence range bar
   * Ensures minimum height of 0.75rem (12px) is maintained
   */
  getCalculatedRangeHeight(): string {
    // Default height is 12px (0.75rem), but ensure minimum is applied
    const defaultHeight = '0.75rem';
    return `max(${this.MIN_RANGE_HEIGHT_REM}rem, ${defaultHeight})`;
  }
}
